import { Controller } from "@hotwired/stimulus"

// Autosave for inputs/textareas/selects inside the side panel.
// Two modes:
//   defer=false (default) — used for row inline cells. Debounced
//     300ms on input; immediate on blur/change. PATCHes via update_inline
//     and dispatches task:field-updated for cross-surface sync.
//   defer=true — used for sidebar/panel fields. Tracks dirty state but
//     does NOT save automatically. Listens for panel:save-all to flush
//     and panel:cancel-all to revert. The panel-form controller raises
//     those events from explicit Save / Cancel buttons. This mode exists
//     because the assignment picker autosaving on the first checkbox
//     click made multi-individual selection in the panel impossible.
export default class extends Controller {
  static values = {
    url: String,
    field: String,
    defer: Boolean
  }

  connect() {
    this.originalValue = this.element.value
    this.lastValue = this.element.value
    this.wasDirty = false
    this.handler = this.onChange.bind(this)
    this.element.addEventListener("input", this.handler)
    this.element.addEventListener("change", this.handler)
    if (!this.deferValue) {
      this.element.addEventListener("blur", () => this.flushNow())
    }

    if (this.deferValue) {
      this.boundSaveAll = () => this.flushNow()
      this.boundCancelAll = () => this.revert()
      document.addEventListener("panel:save-all", this.boundSaveAll)
      document.addEventListener("panel:cancel-all", this.boundCancelAll)
    }
  }

  disconnect() {
    this.element.removeEventListener("input", this.handler)
    this.element.removeEventListener("change", this.handler)
    if (this.deferValue) {
      document.removeEventListener("panel:save-all", this.boundSaveAll)
      document.removeEventListener("panel:cancel-all", this.boundCancelAll)
    }
  }

  onChange() {
    if (this.deferValue) {
      // Defer mode: only notify the panel-form controller when our
      // dirty STATE changes (e.g. clean→dirty or dirty→clean). The
      // panel-form counter would over-count if we fired on every
      // keystroke.
      const isDirty = this.element.value !== this.originalValue
      if (isDirty !== this.wasDirty) {
        this.wasDirty = isDirty
        document.dispatchEvent(new CustomEvent("panel:dirty", {
          detail: { dirty: isDirty }
        }))
      }
      return
    }
    this.scheduleSave()
  }

  scheduleSave() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.flushNow(), 300)
  }

  revert() {
    this.element.value = this.originalValue
    this.lastValue = this.originalValue
    this.wasDirty = false
  }

  async flushNow() {
    clearTimeout(this.timer)
    const value = this.element.value
    if (value === this.lastValue) return
    this.lastValue = value
    this.originalValue = value

    // Find the task ID from the panel content's frame URL (set by
    // intern-task-row when the panel opens). Used to scope the
    // task:field-updated event for cross-surface sync.
    const taskId = this.taskIdFromPanel()

    document.dispatchEvent(new CustomEvent("panel:saving"))

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: this.headers(),
        body: JSON.stringify({ field: this.fieldValue, value: value })
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        console.error("autosave failed:", data.errors || response.status)
        document.dispatchEvent(new CustomEvent("panel:save-error"))
        return
      }
      const data = await response.json().catch(() => ({}))
      document.dispatchEvent(new CustomEvent("panel:saved"))

      // Server can signal "this change requires a full re-render"
      // (e.g., assignment changes that ripple completion records).
      if (data.reload) {
        window.location.reload()
        return
      }

      // Tell the row's cells to refresh without a reload.
      document.dispatchEvent(new CustomEvent("task:field-updated", {
        detail: { taskId: taskId, field: this.fieldValue, value: value }
      }))
    } catch (err) {
      console.error("autosave network error:", err)
      document.dispatchEvent(new CustomEvent("panel:save-error"))
    }
  }

  taskIdFromPanel() {
    const frame = document.getElementById("side_panel_content")
    if (!frame?.src) return null
    const match = frame.src.match(/intern_tasks\/(\d+)/)
    return match ? match[1] : null
  }

  headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "Accept": "application/json"
    }
  }
}
